import {
    Button,
    ClickAwayListener,
    MenuItem,
    Popper,
    Paper,
    styled,
    TextField,
    Tooltip,
    FormControl,
    Grow,
    ButtonGroup,
    Grid,
    Typography,
    IconButton,
    Box,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    ChangeEvent, MouseEvent, useEffect, useRef, useState, ReactNode, useMemo,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CSSGrid } from 'src/components/common/CSSGrid';
import {
    ArrowDropDown, Remove,
} from '@mui/icons-material';
import { ASP_SEARCH_BACKENDS, ASP_SEARCH_BACKEND, ASP_DEFAULT_BACKEND } from 'src/constants';
import { SaveLoadingButton } from '../buttons/SaveLoadingButton';

interface ExperimentFlag {
    id: string;
    key: string;
    value: string;
}

export interface ButtonGroupOptions {
    searchBackend: ASP_SEARCH_BACKEND;
    experimentFlags: Record<string, string>;
}

export interface ButtonGroupWithPopperProps {
    buttonText: ReactNode;
    buttonIcon?: ReactNode;
    tooltipText: string;
    isDisabled?: boolean;
    showExperimentalSwitch?: boolean;
    includeExperimental?: boolean;
    onIncludeExperimentalChange?: (include: boolean) => void;
    onOptionsChange?: (options: ButtonGroupOptions) => void;
    onButtonClick: (options: ButtonGroupOptions) => Promise<void>;
}

const StyledPopper = styled(Popper)(() => ({
    width: 'fit-content',
    zIndex: 1000,
}));

const StyledButton = styled(Button)(() => ({
    whiteSpace: 'nowrap',
    '&.Mui-disabled': {
        pointerEvents: 'auto',
    },
}));

const StyledSaveLoadingButton = styled(SaveLoadingButton)(() => ({
    whiteSpace: 'nowrap',
    '&.Mui-disabled': {
        pointerEvents: 'auto',
    },
}));

const StyledButtonGroup = styled(ButtonGroup)(() => ({
    width: 'min-content',
}));

const StyledCSSGrid = styled(CSSGrid)(({ theme }) => ({
    gap: theme.spacing(6),
    padding: theme.spacing(6),
    width: theme.spacing(120),
}));

const ExperimentFlagRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2),
}));

const DeleteButtonContainer = styled(Box)(({ theme }) => ({
    width: theme.spacing(5),
    display: 'flex',
    justifyContent: 'center',
}));

export const ButtonGroupWithPopper = (props: ButtonGroupWithPopperProps): JSX.Element => {
    const {
        buttonText,
        buttonIcon,
        tooltipText,
        isDisabled = false,
        showExperimentalSwitch = false,
        includeExperimental = false,
        onIncludeExperimentalChange,
        onOptionsChange,
        onButtonClick,
    } = props;

    const [searchBackend, setSearchBackend] = useState<ASP_SEARCH_BACKEND>(ASP_DEFAULT_BACKEND);
    const [experimentFlags, setExperimentFlags] = useState<ExperimentFlag[]>([{ id: uuidv4(), key: '', value: '' }]);
    const [open, setOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    const currentOptions: ButtonGroupOptions = useMemo(() => ({
        searchBackend,
        experimentFlags: experimentFlags
            .filter((flag) => flag.key.trim() && flag.value.trim())
            .reduce((acc, flag) => {
                acc[flag.key.trim()] = flag.value.trim();
                return acc;
            }, {} as Record<string, string>),
    }), [searchBackend, experimentFlags]);

    useEffect(() => {
        if (onOptionsChange) {
            onOptionsChange(currentOptions);
        }
    }, [searchBackend, experimentFlags, onOptionsChange, currentOptions]);

    const handleToggle = (): void => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event): void => {
        if ((anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) || menuOpen) {
            return;
        }
        setOpen(false);
    };

    const handleSearchBackendChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setSearchBackend(event.target.value as ASP_SEARCH_BACKEND);
    };

    const handleExperimentFlagChange = (id: string, field: 'key' | 'value', value: string): void => {
        const newFlags = experimentFlags.map((flag) => (flag.id === id ? { ...flag, [field]: value } : flag));

        setExperimentFlags(newFlags);
    };

    const handleRemoveExperimentFlag = (id: string): void => {
        if (experimentFlags.length > 1) {
            const newFlags = experimentFlags.filter((flag) => flag.id !== id);

            setExperimentFlags(newFlags);
        }
    };

    const handleIncludeExperimentalChange = (event: ChangeEvent<HTMLInputElement>): void => {
        if (onIncludeExperimentalChange) {
            onIncludeExperimentalChange(event.target.checked);
        }
    };

    const handleButtonClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();
        setOpen(false);

        await onButtonClick(currentOptions);
    };

    const toggleMenuOpen = (): void => {
        setMenuOpen((prev) => !prev);
    };

    useEffect(() => {
        const allRowsFilled = experimentFlags.every((flag) => flag.key.trim() && flag.value.trim());

        if (allRowsFilled && experimentFlags.length > 0) {
            setExperimentFlags([...experimentFlags, { id: uuidv4(), key: '', value: '' }]);
        }
    }, [experimentFlags]);

    return (
        <FormControl>
            <StyledButtonGroup
                aria-label={tooltipText}
                ref={anchorRef}
                variant="outlined"
            >
                <Tooltip title={tooltipText}>
                    <StyledSaveLoadingButton
                        disabled={isDisabled}
                        startIcon={buttonIcon}
                        onClick={handleButtonClick}
                    >
                        {buttonText}
                    </StyledSaveLoadingButton>
                </Tooltip>
                <Tooltip title="Click to configure parameters">
                    <StyledButton
                        aria-controls={open ? 'button-group-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        aria-label="Click to configure parameters"
                        sx={{ px: 0 }}
                        onClick={handleToggle}
                    >
                        <ArrowDropDown />
                    </StyledButton>
                </Tooltip>
            </StyledButtonGroup>
            <StyledPopper
                transition
                anchorEl={anchorRef.current}
                disablePortal={false}
                open={open}
                placement="bottom-end"
            >
                {({ TransitionProps, placement }): JSX.Element => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper elevation={16}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <StyledCSSGrid>
                                    <Grid container direction="column" spacing={3}>
                                        {showExperimentalSwitch && (
                                            <Grid item>
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={includeExperimental}
                                                            size="small"
                                                            onChange={handleIncludeExperimentalChange}
                                                        />
                                                    )}
                                                    label="Include experimental test runs"
                                                />
                                            </Grid>
                                        )}
                                        <Grid item>
                                            <TextField
                                                fullWidth
                                                select
                                                label="Search Backend"
                                                SelectProps={{
                                                    onClose: toggleMenuOpen,
                                                    onOpen: toggleMenuOpen,
                                                }}
                                                size="small"
                                                value={searchBackend}
                                                onChange={handleSearchBackendChange}
                                            >
                                                {ASP_SEARCH_BACKENDS.map((backend) => (
                                                    <MenuItem key={backend} value={backend}>
                                                        {backend}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item>
                                            <Typography gutterBottom variant="subtitle2">
                                                Experiment Flags
                                            </Typography>
                                            <Box>
                                                {experimentFlags.map((flag) => (
                                                    <ExperimentFlagRow key={flag.id}>
                                                        <TextField
                                                            label="Key"
                                                            size="small"
                                                            sx={{ flex: 1 }}
                                                            value={flag.key}
                                                            onChange={(e): void => handleExperimentFlagChange(flag.id, 'key', e.target.value)}
                                                        />
                                                        <TextField
                                                            label="Value"
                                                            size="small"
                                                            sx={{ flex: 1 }}
                                                            value={flag.value}
                                                            onChange={(e): void => handleExperimentFlagChange(flag.id, 'value', e.target.value)}
                                                        />
                                                        <DeleteButtonContainer>
                                                            {experimentFlags.length > 1 && (flag.key.trim().length > 0
                                                                || flag.value.trim().length > 0) && (
                                                                <IconButton
                                                                    disabled={experimentFlags.length === 1}
                                                                    size="small"
                                                                    onClick={(): void => {
                                                                        handleRemoveExperimentFlag(flag.id);
                                                                    }}
                                                                >
                                                                    <Remove />
                                                                </IconButton>
                                                            )}
                                                        </DeleteButtonContainer>
                                                    </ExperimentFlagRow>
                                                ))}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </StyledCSSGrid>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </StyledPopper>
        </FormControl>
    );
};
